import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import "./SendCampaign.css";

const SendTextCampaign = () => {
  const [mobile_no, setMobile_No] = useState("");
  const [count, setCount] = useState(0);
  const [numbercounter, setNumberCounter] = useState("");
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contactGroupData, setContactGroupData] = useState([]); // Added for group import
  const [userProfile, setUserProfile] = useState(null);
  
  // Calculate message count based on template content (160 characters = 1 message)
  const messageCount = selectedTemplate && selectedTemplate.templateFormat 
    ? Math.ceil(selectedTemplate.templateFormat.length / 160) 
    : 0;
  
  // Calculate total credits required using per-user text_rate config
  const textCreditsPerUnit = userProfile ? parseFloat(userProfile.text_rate) || 1 : 1;
  const totalCreditsRequired = textCreditsPerUnit * messageCount * count;

  const token = localStorage.getItem("token");
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const fetchUserProfile = () => {
    axios
      .get(`${BASE_URL}auth/view-profile`, {
        headers: { "x-access-token": token, version: "1.0.0", "Content-Type": "Application/json" },
      })
      .then((response) => {
        if (response.data.success) setUserProfile(response.data.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchTemplates();
    fetchContactGroups();
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const theme = localStorage.getItem("theme");
    document.body.className = theme;
    if (numbercounter.length == 0) {
      setNumberCounter(0);
    }
  }, [count]);

  const fetchTemplates = () => {
    axios
      .post(
        `${BASE_URL}admin/getAllTextTemplates`,
        { statusFilter: "approved" },
        {
          headers: {
            "x-access-token": `${token}`,
            version: "1.0.0",
            "Content-Type": "Application/json",
          },
        }
      )
      .then((response) => {
        setTemplates(response.data.data.data || []);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchContactGroups = () => { // New function for group import
    axios
      .post(
        `${BASE_URL}admin/getAllContactGroup`,
        { page: 1 },
        {
          headers: {
            "x-access-token": `${token}`,
            version: "1.0.0",
            "Content-Type": "Application/json",
          },
        }
      )
      .then((response) => {
        if (response.data.success !== false) {
          setContactGroupData(response.data.data);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const RowCount = (text) => {
    // Filter to allow only digits and newlines (remove all letters and special chars except newlines)
    const filteredText = text.replace(/[^0-9\n\r]/g, '');
    var lines = filteredText.split(/\r|\r\n|\n/);
    setCount(lines.length);
    setNumberCounter(count);
    setMobile_No(filteredText);
  };

  const GroupRowCount = (text) => { // New function for group import
    // Filter to allow only digits and newlines (remove all letters and special chars except newlines)
    const filteredText = text.replace(/[^0-9\n\r]/g, '');
    var lines = filteredText.split(/\r|\r\n|\n/);

    setCount(lines.length);

    setNumberCounter(count);
    setMobile_No(filteredText);
  };

  const sanitizeNumbers = (rawValues) => {
    const seen = new Set();
    const result = [];
    for (const val of rawValues) {
      const num = String(val).trim().replace(/[^0-9]/g, "");
      if (num && !seen.has(num)) {
        seen.add(num);
        result.push(num);
      }
    }
    return result;
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();

    if (fileName.endsWith(".csv")) {
      Papa.parse(file, {
        skipEmptyLines: true,
        complete: (results) => {
          const rawValues = results.data.map((row) => row[0]).filter((v) => v !== undefined && v !== null);
          const numbers = sanitizeNumbers(rawValues);
          const text = numbers.join("\n");
          setMobile_No(text);
          setCount(numbers.length);
        },
      });
    } else if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const data = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const rawValues = rows.map((row) => row[0]).filter((v) => v !== undefined && v !== null);
        const numbers = sanitizeNumbers(rawValues);
        const text = numbers.join("\n");
        setMobile_No(text);
        setCount(numbers.length);
      };
      reader.readAsArrayBuffer(file);
    } else {
      toast.error("Please upload a .csv, .xlsx, or .xls file");
    }
    e.target.value = "";
  };

  const submitTextMessage = () => {
    setLoading(true);
    if (!selectedTemplate) {
      toast.error("Template selection is mandatory");
      setLoading(false);
      return;
    }
    if (!mobile_no) {
      toast.error("Mobile numbers are mandatory");
      setLoading(false);
      return;
    }
    axios
      .post(
        `${BASE_URL}admin/addSendMessage`,
        {
          contact_no: mobile_no,
          msg_count: count,
          template_id: selectedTemplate._id, // Send template_id
          media_type: "TEXT",
        },
        {
          headers: {
            "x-access-token": `${token}`,
            version: "1.0.0",
            "Content-Type": "Application/json",
          },
        }
      )
      .then((response) => {
        if (response.data.success) {
          toast.success("Text message sent successfully");
          setMobile_No("");
          setCount(0);
          setNumberCounter("");
          setSelectedTemplate(null);
        } else {
          toast.error(response.data.message || "Failed to send text message");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to send text message");
        setLoading(false);
      });
  };

  return (
    <div className="send-campaign-container">
      <div className="send-campaign-card card">
        <div className="send-campaign-header card-header">
          <h6>Send Text Message Campaign</h6>
        </div>

        {/* Campaign Summary notification banner */}
        {count > 0 && selectedTemplate && (() => {
          const availableCredits = userProfile ? parseFloat(userProfile.text_balance) || 0 : 0;
          const insufficient = availableCredits < totalCreditsRequired;
          return (
            <div style={{
              background: insufficient ? "#fff3e0" : "#e8f5e9",
              borderBottom: `1px solid ${insufficient ? "#ffcc80" : "#a5d6a7"}`,
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              fontSize: 13,
            }}>
              <i className={`fa ${insufficient ? "fa-exclamation-triangle" : "fa-info-circle"}`}
                style={{ color: insufficient ? "#e65100" : "#2e7d32", fontSize: 15 }} />
              <span><strong style={{ color: insufficient ? "#e65100" : "#2e7d32" }}>Campaign Summary</strong></span>
              <span style={{ color: "#555" }}>Required: <strong>₹{(+totalCreditsRequired).toFixed(2)}</strong> (₹{(+textCreditsPerUnit).toFixed(2)} × {messageCount} msg{messageCount > 1 ? "s" : ""} × {count} contacts)</span>
              <span style={{ color: insufficient ? "#c62828" : "#1b5e20" }}>
                Available: <strong>₹{(+availableCredits).toFixed(2)}</strong>
              </span>
              {insufficient && <span style={{ color: "#c62828" }}>— Insufficient ₹.</span>}
            </div>
          );
        })()}

        <div className="send-campaign-body card-body">
          {/* Mobile Numbers */}
          <div className="form-group-responsive">
            <label className="form-label-responsive">Mobile Numbers</label>
            <textarea
              className="form-control-responsive"
              rows="4"
              placeholder="Enter 10 digits mobile number here)"
              value={mobile_no}
              onChange={(e) => RowCount(e.target.value)}
            />
          </div>

          {/* Template Selection */}
          <div className="form-group-responsive">
            <label className="form-label-responsive">Select Template:</label>
            <select
              className="form-control-responsive"
              value={selectedTemplate ? selectedTemplate._id : ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                const template = templates.find((t) => t._id === selectedId);
                setSelectedTemplate(template || null);
              }}
            >
              <option value="" disabled>
                Select text template
              </option>
              {templates.map((template) => (
                <option key={template._id} value={template._id}>
                  {template.templateName}
                </option>
              ))}
            </select>
          </div>

          {/* Numbers Count */}
          <div className="form-group-responsive">
            <label className="form-label-responsive">Numbers Count:</label>
            <input
              type="text"
              className="form-control-responsive"
              name="numbercounter"
              value={count}
              disabled
            />
          </div>

          {/* Group Import */}
          <div className="form-group-responsive">
            <label className="form-label-responsive">Group Import</label>
            <select
              className="form-control-responsive"
              onChange={(e) => GroupRowCount(e.target.value)}
              defaultValue=""
            >
              <option value="" disabled>--select--</option>
              {contactGroupData &&
                contactGroupData.map((item, index) => (
                  <option key={index} value={item.contact_no}>
                    {item.group_name}
                  </option>
                ))}
            </select>
          </div>

          {/* CSV / Excel Import */}
          <div className="form-group-responsive">
            <label className="form-label-responsive">CSV / Excel Import</label>
            <input
              type="file"
              className="form-control-responsive"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileImport}
            />
          </div>

          {/* Submit Button */}
          <div className="submit-btn-container">
            {loading ? (
              <button type="button" className="btn btn-primary " disabled>
                <i className="fa fa-spinner fa-spin  "></i>
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary "
                onClick={submitTextMessage}
              >
                Submit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendTextCampaign;